/**
 * magnifier-core.js — the viewer engine behind /magnifier ("Лупа онлайн").
 *
 * WHY THIS FILE EXISTS
 * ────────────────────
 * The previous magnifier zoomed by CENTRE-CROPPING the source into the canvas:
 *
 *     var cropW = sw / zoom, cx = (sw - cropW) / 2;   // always the middle
 *     ctx.drawImage(src, cx, cy, cropW, cropH, 0, 0, cw, ch);
 *
 * There is no offset in that expression, so at any zoom you could only ever see
 * the centre of the frame. Measured 2026-09-07 on the live tool (Pixel 7 UA,
 * fx-smallprint.png): mouse drag and touch drag both left `canvas.toDataURL()`
 * byte-identical — panning was not "hard", it was absent. A magnifier you
 * cannot aim is the root defect; everything else was cosmetics on top of it.
 *
 * So the engine is built around ONE affine transform (scale + offset + rotation
 * + mirror) that every source and every interaction writes into, and a render
 * pass that is the SAME for camera, file and screen.
 *
 * THREE SOURCES, ONE VIEWER
 * ─────────────────────────
 * Yandex Wordstat for «лупа онлайн» (6 753/mo, snapshot 07.08–05.09.2026) splits
 * cleanly into three jobs, and they are three SOURCES of one instrument, not
 * three tools: camera («лупа онлайн через камеру», «камера лупа онлайн»),
 * file («фото лупа онлайн» 58, «лупа онлайн для увеличения фото» 31) and screen
 * («экранная лупа онлайн» 19). Anything that differs between them is confined
 * to `setSource()`; the transform, the palettes, the loupe and the snapshot
 * never learn which one is active.
 *
 * COLOUR IS DRAWN, NOT STYLED
 * ───────────────────────────
 * The old tool set `canvas.style.filter`. That is a CSS effect on the ELEMENT:
 * it never reaches `toBlob()`, so a saved frame came out unfiltered — the
 * high-contrast view a low-vision user set up was exactly the thing the file
 * did not contain. Here every colour mode is `ctx.filter` plus composite fills,
 * i.e. part of the drawing, so the snapshot is what the screen showed.
 *
 * Duotone recipe (the classic CCTV palettes real electronic magnifiers ship):
 * after `grayscale(1)`, a `screen` fill lifts the blacks to colour S and a
 * `multiply` fill pulls the whites down to colour M, so the image ends up
 * spanning S→M. `invert(1)` before that swaps which end the text sits on. Both
 * are GPU composites — no per-pixel JS on the frame path.
 */
(function () {
'use strict';

/* Zoom ceiling. Wordstat has people asking for «лупа увеличительная 40 кратная
   онлайн», and a jeweller's loupe is 10×–30×, so 8× (the old cap) stopped short
   of the demand it was serving. 40 is also where a 12 MP phone photo runs out
   of real pixels, which is why the readout starts saying "интерполяция". */
var MIN_SCALE = 1, MAX_SCALE = 40;

/* Above this the picture is mostly upscaled, so nearest-neighbour is the honest
   filter: it shows the pixel grid instead of inventing smooth edges. Chosen so
   that reading (which wants smoothing) keeps it and inspecting loses it. */
var CRISP_AT = 8;

var PALETTES = {
    normal:  { filter: '' },
    contrast:{ filter: 'contrast(1.75) brightness(1.04)' },
    invert:  { filter: 'invert(1)' },
    gray:    { filter: 'grayscale(1) contrast(1.35)' },
    bw:      { filter: 'grayscale(1) contrast(6) brightness(1.08)' },
    // Low-vision duotones. `inv` decides whether text ends up light or dark.
    yob:     { filter: 'grayscale(1) contrast(1.7)', inv: true,  screen: '#000000', multiply: '#ffd84d' },
    boy:     { filter: 'grayscale(1) contrast(1.7)', inv: false, screen: '#000000', multiply: '#ffd84d' },
    wob:     { filter: 'grayscale(1) contrast(1.7)', inv: true,  screen: '#0a2a6b', multiply: '#ffffff' },
    bow:     { filter: 'grayscale(1) contrast(1.7)', inv: false, screen: '#0a2a6b', multiply: '#ffffff' }
};

function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

function Magnifier(opts) {
    opts = opts || {};
    this.canvas = opts.canvas;
    this.ctx = this.canvas.getContext('2d', { alpha: false, desynchronized: true });
    this.onChange = opts.onChange || function () {};
    this.onFrame = opts.onFrame || null;

    this.src = null;          // { el, w, h, live }
    /* Магнификация раскладывается на ДВА множителя, и путать их нельзя.
       `scale` — цифровая, её делает преобразование канвы. `extScale` — та, что
       уже впечатана в сам источник железом камеры (`applyConstraints({zoom})`).
       Человек видит и просит ПРОИЗВЕДЕНИЕ. Пока они складывались наивно, зум
       камеры 3× вместе с цифровым 3× давал на экране 9× под подписью «300 %» —
       прибор врал ровно там, где число и есть его обещание. Оптику берём
       первой (это настоящие пиксели), цифру — остатком. */
    this.scale = 1;
    this.extScale = 1;
    this.onZoomRequest = null;  // (total) => ext, который источник смог дать
    this.ox = 0; this.oy = 0; // offset in SOURCE pixels of the view centre
    this.rot = 0;             // 0 | 90 | 180 | 270
    this.mirror = false;
    this.palette = 'normal';
    this.brightness = 1;
    this.contrast = 1;
    this.smoothOverride = null; // null = auto, true/false = forced
    this.frozen = false;

    // Loupe: 'off' | 'pointer' | 'center'
    this.lensMode = 'off';
    this.lensZoom = 2.5;
    this.lensSize = 160;
    this.lensShape = 'circle';
    this.lensX = 0; this.lensY = 0; this.lensActive = false;

    /* Reading guide — the band that keeps your place on a line of text. Every
       desktop screen magnifier ships one (ZoomText, Windows Magnifier), no web
       magnifier does, and at 10x on a phone losing the line you were on is the
       single most common way reading stalls. It is an overlay, not a mode: it
       costs two fills and rides the same pointer the loupe does. */
    this.guide = false;
    this.guideH = 64;

    this._raf = 0;
    this._frozenBmp = null;
    /* A frozen frame may carry MORE pixels than the preview it replaced
       (ImageCapture.takePhoto gives the sensor, not the 1080p stream). Geometry
       stays in preview coordinates — only the honesty readout needs to know. */
    this._frozenScale = 1;
    this._dpr = 1;
    this._dirty = true;
    this._frameSeq = 0;   // bumped by requestVideoFrameCallback
    this._drawnSeq = -1;
}

/* ── zoom scale ──────────────────────────────────────────────────────────────
 * A magnifier spends most of its life between 1x and 4x, and a LINEAR 1..40
 * slider gives that range 7.7 % of its travel (measured on the old build) —
 * every useful setting crammed into the first 3 mm under a fingertip. The
 * control is logarithmic, so equal travel is equal RATIO: the distance from 1x
 * to 2x equals the distance from 20x to 40x, which is what "twice as big"
 * means to the hand holding it.
 */
Magnifier.sliderToZoom = function (pos) {
    var t = clamp(pos, 0, 1000) / 1000;
    return MIN_SCALE * Math.pow(MAX_SCALE / MIN_SCALE, t);
};
Magnifier.zoomToSlider = function (z) {
    var t = Math.log(clamp(z, MIN_SCALE, MAX_SCALE) / MIN_SCALE) / Math.log(MAX_SCALE / MIN_SCALE);
    return Math.round(t * 1000);
};

/* ── sources ─────────────────────────────────────────────────────────────── */

Magnifier.prototype.setSource = function (el, w, h, live) {
    this.releaseFreeze();
    this.src = { el: el, w: w, h: h, live: !!live };
    this.rot = 0; this.mirror = false; this.frozen = false;
    this.fit();
    this._loop();
    this.onChange();
};

Magnifier.prototype.clearSource = function () {
    this.releaseFreeze();
    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = 0; }
    this._vfcSrc = null;
    this.src = null;
    this.onChange();
};

/** Source dimensions after rotation — everything downstream uses these. */
Magnifier.prototype.viewW = function () { if (!this.src) return 0; return (this.rot % 180) ? this.src.h : this.src.w; };
Magnifier.prototype.viewH = function () { if (!this.src) return 0; return (this.rot % 180) ? this.src.w : this.src.h; };

/* ── transform ───────────────────────────────────────────────────────────── */

/**
 * Scale at which the whole frame fits the canvas. This is scale === 1 for the
 * user: the readout shows "100 %" when the entire picture is visible, which is
 * what "увеличение 200 %" means to someone who just opened a photo.
 */
Magnifier.prototype._baseScale = function () {
    var cw = this.canvas.width / this._dpr, ch = this.canvas.height / this._dpr;
    var vw = this.viewW(), vh = this.viewH();
    if (!vw || !vh || !cw || !ch) return 1;
    return Math.min(cw / vw, ch / vh);
};

Magnifier.prototype.fit = function () {
    if (this.onZoomRequest) { try { this.onZoomRequest(1); } catch (_) {} }
    this.extScale = 1;
    this.scale = 1;
    this.ox = this.viewW() / 2;
    this.oy = this.viewH() / 2;
    this._dirty = true;
    this.onChange();
};

/** 1:1 — one source pixel per CSS pixel. The "actual size" of pixspy's 1:1. */
Magnifier.prototype.actualSize = function () {
    var b = this._baseScale();
    this.setTotal(b ? 1 / b : 1);
};

Magnifier.prototype.setScale = function (s, anchorX, anchorY) {
    var old = this.scale;
    var next = clamp(s, MIN_SCALE, MAX_SCALE / (this.extScale || 1));
    if (next === old) return;
    // Zoom about a point: keep the source pixel under the anchor stationary.
    if (anchorX != null && anchorY != null) {
        var p = this.canvasToSource(anchorX, anchorY);
        this.scale = next;
        var q = this.canvasToSource(anchorX, anchorY);
        this.ox += (p.x - q.x);
        this.oy += (p.y - q.y);
    } else {
        this.scale = next;
    }
    this._clampOffset();
    this._dirty = true;
    this.onChange();
};

/** Полная магнификация, которую видит человек: цифровая × аппаратная. */
Magnifier.prototype.total = function () { return this.scale * this.extScale; };

/**
 * Запросить ПОЛНУЮ магнификацию. Единственная дверь для всех органов зума —
 * ползунок, кнопки, колесо, щипок, двойной тап, — чтобы разложение на оптику и
 * цифру было одно на всех, а не зависело от того, чем именно покрутили.
 */
Magnifier.prototype.setTotal = function (total, ax, ay) {
    total = clamp(total, MIN_SCALE, MAX_SCALE);
    var ext = 1;
    if (this.onZoomRequest) {
        try { ext = this.onZoomRequest(total) || 1; } catch (_) { ext = 1; }
    }
    ext = clamp(ext, 1, total);
    this.extScale = ext;
    this.setScale(total / ext, ax, ay);
};

Magnifier.prototype.zoomBy = function (factor, ax, ay) { this.setTotal(this.total() * factor, ax, ay); };

Magnifier.prototype.panBy = function (dxCanvas, dyCanvas) {
    var eff = this._baseScale() * this.scale;
    if (!eff) return;
    // Undo rotation/mirror so a drag right always moves the picture right.
    var dx = dxCanvas / eff, dy = dyCanvas / eff;
    if (this.mirror) dx = -dx;
    var r = ((this.rot % 360) + 360) % 360, sx, sy;
    if (r === 0)        { sx = -dx; sy = -dy; }
    else if (r === 90)  { sx = -dy; sy =  dx; }
    else if (r === 180) { sx =  dx; sy =  dy; }
    else                { sx =  dy; sy = -dx; }
    this.ox += sx; this.oy += sy;
    this._clampOffset();
    this._dirty = true;
    this.onChange();
};

/**
 * Keep the picture reachable but never let it drift off screen entirely.
 * When the frame is smaller than the viewport on an axis it is pinned centred —
 * otherwise a fitted photo would slide away under a stray drag.
 */
Magnifier.prototype._clampOffset = function () {
    var vw = this.viewW(), vh = this.viewH();
    if (!vw || !vh) return;
    var eff = this._baseScale() * this.scale;
    var cw = (this.canvas.width / this._dpr) / eff, ch = (this.canvas.height / this._dpr) / eff;
    if (cw >= vw) this.ox = vw / 2; else this.ox = clamp(this.ox, cw / 2, vw - cw / 2);
    if (ch >= vh) this.oy = vh / 2; else this.oy = clamp(this.oy, ch / 2, vh - ch / 2);
};

/** Canvas CSS px → source px (post-rotation frame). */
Magnifier.prototype.canvasToSource = function (cx, cy) {
    var eff = this._baseScale() * this.scale;
    var cw = this.canvas.width / this._dpr, ch = this.canvas.height / this._dpr;
    var x = (cx - cw / 2) / eff, y = (cy - ch / 2) / eff;
    if (this.mirror) x = -x;
    return { x: this.ox + x, y: this.oy + y };
};

/* ── rotation / mirror ───────────────────────────────────────────────────── */

Magnifier.prototype.rotate = function (deg) {
    var vw = this.viewW(), vh = this.viewH();
    var fx = vw ? this.ox / vw : 0.5, fy = vh ? this.oy / vh : 0.5;
    this.rot = (((this.rot + deg) % 360) + 360) % 360;
    // Carry the aim through the turn instead of jumping back to the middle.
    var nw = this.viewW(), nh = this.viewH();
    if (deg === 90)       { this.ox = (1 - fy) * nw; this.oy = fx * nh; }
    else if (deg === -90) { this.ox = fy * nw;       this.oy = (1 - fx) * nh; }
    else                  { this.ox = (1 - fx) * nw; this.oy = (1 - fy) * nh; }
    this._clampOffset();
    this._dirty = true;
    this.onChange();
};

Magnifier.prototype.toggleMirror = function () { this.mirror = !this.mirror; this._dirty = true; this.onChange(); };

/* ── freeze ──────────────────────────────────────────────────────────────── */

/**
 * Freeze snapshots the SOURCE frame, not the view, so zoom/pan/palette stay
 * live afterwards. At 20× a hand-held phone is unreadable from shake alone;
 * freezing is what makes high magnification usable, which is why every physical
 * electronic magnifier has the button.
 */
Magnifier.prototype.freeze = function () {
    if (!this.src || !this.src.live || this.frozen) return;
    var c = document.createElement('canvas');
    c.width = this.src.w; c.height = this.src.h;
    c.getContext('2d').drawImage(this.src.el, 0, 0, this.src.w, this.src.h);
    this._frozenBmp = c;
    this._frozenScale = 1;
    this.frozen = true;
    this._dirty = true;
    this.onChange();
};

Magnifier.prototype.unfreeze = function () { this.releaseFreeze(); this._dirty = true; this.onChange(); };
Magnifier.prototype.releaseFreeze = function () { this._frozenBmp = null; this._frozenScale = 1; this.frozen = false; };

/**
 * Freeze onto a picture that is not the preview — the full-resolution still
 * `ImageCapture.takePhoto()` hands back on Chrome/Android. The transform keeps
 * working in preview coordinates; only `sourceRatio()` learns that there are
 * now more real pixels behind each of them, so the "интерполяция" mark stops
 * firing on detail that genuinely exists.
 */
Magnifier.prototype.freezeWith = function (bmp, w, h) {
    if (!this.src || !bmp) return false;
    this._frozenBmp = bmp;
    this._frozenScale = (w && this.src.w) ? (w / this.src.w) : 1;
    this.frozen = true;
    this._dirty = true;
    this.onChange();
    return true;
};

Magnifier.prototype._drawable = function () {
    if (this.frozen && this._frozenBmp) return this._frozenBmp;
    return this.src ? this.src.el : null;
};

/* ── render ──────────────────────────────────────────────────────────────── */

Magnifier.prototype.resize = function () {
    var host = this.canvas.parentElement;
    if (!host) return;
    var r = host.getBoundingClientRect();
    if (!r.width || !r.height) return;
    /* Cap the backing store. The pixel cost of a frame is the CANVAS area, not
       the source's — a 48 MP photo and a 720p webcam cost the same here — so
       this is the one number that decides whether a cheap phone keeps 60 fps. */
    var dpr = clamp(window.devicePixelRatio || 1, 1, 2);
    var maxPx = 2600000;
    if (r.width * r.height * dpr * dpr > maxPx) dpr = Math.sqrt(maxPx / (r.width * r.height));
    var w = Math.max(1, Math.round(r.width * dpr)), h = Math.max(1, Math.round(r.height * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
        this.canvas.width = w; this.canvas.height = h;
        this._dpr = dpr;
        this._clampOffset();
        this._dirty = true;
    }
};

Magnifier.prototype._loop = function () {
    var self = this;
    if (this._raf) cancelAnimationFrame(this._raf);

    /* A live source is redrawn when a NEW frame arrives, not 60 times a second.
       `requestVideoFrameCallback` is the only API that says "a frame landed";
       without it we fall back to redrawing every tick, which is what this did
       before. On a 30 fps camera against a 60 Hz display that halves the draw
       work — and on a screen share of a still document it takes it to zero,
       which is the case a magnifier sits in for minutes at a time. */
    var el = this.src && this.src.el;
    if (this.src && this.src.live && el && el.requestVideoFrameCallback) {
        this._vfcSrc = el;
        (function frame() {
            if (self._vfcSrc !== el || !self.src) return;
            self._frameSeq++;
            try { el.requestVideoFrameCallback(frame); } catch (_) {}
        })();
    } else {
        this._vfcSrc = null;
    }

    (function tick() {
        self._raf = requestAnimationFrame(tick);
        var live = self.src && self.src.live && !self.frozen;
        var fresh = self._vfcSrc ? (self._frameSeq !== self._drawnSeq) : live;
        if (!fresh && !self._dirty) return;
        self._dirty = false;
        self._drawnSeq = self._frameSeq;
        self.draw();
    })();
};

Magnifier.prototype.requestDraw = function () { this._dirty = true; };

Magnifier.prototype._filterString = function (pal, plain) {
    var f = [];
    if (pal.filter) f.push(pal.filter);
    if (pal.inv) f.push('invert(1)');
    if (plain) return f.join(' ');
    if (this.brightness !== 1) f.push('brightness(' + this.brightness.toFixed(2) + ')');
    if (this.contrast !== 1) f.push('contrast(' + this.contrast.toFixed(2) + ')');
    return f.join(' ');
};

Magnifier.prototype.smoothing = function () {
    if (this.smoothOverride !== null) return this.smoothOverride;
    return this.scale < CRISP_AT;
};

/**
 * @param {CanvasRenderingContext2D} ctx target
 * @param {number} cw,ch  target size in CSS px
 * @param {number} scaleMul extra magnification (the loupe passes >1)
 * @param {number} cxSrc,cySrc source point to centre on
 */
Magnifier.prototype._paint = function (ctx, cw, ch, scaleMul, cxSrc, cySrc, palName) {
    var el = this._drawable();
    if (!el) return;
    var pal = PALETTES[palName || this.palette] || PALETTES.normal;
    var eff = this._baseScale() * this.scale * (scaleMul || 1);
    var sm = this.smoothing() && (this.scale * (scaleMul || 1)) < CRISP_AT;

    ctx.save();
    ctx.imageSmoothingEnabled = sm;
    if (sm) ctx.imageSmoothingQuality = 'high';
    ctx.filter = this._filterString(pal, palName === 'normal') || 'none';

    ctx.translate(cw / 2, ch / 2);
    if (this.mirror) ctx.scale(-1, 1);
    if (this.rot) ctx.rotate(this.rot * Math.PI / 180);
    ctx.scale(eff, eff);
    // After rotation we are back in UNROTATED source space, so undo the swap.
    var sw = this.src.w, sh = this.src.h;
    var r = ((this.rot % 360) + 360) % 360;
    var ux, uy;
    if (r === 0)        { ux = cxSrc;            uy = cySrc; }
    else if (r === 90)  { ux = cySrc;            uy = sh - cxSrc; }
    else if (r === 180) { ux = sw - cxSrc;       uy = sh - cySrc; }
    else                { ux = sw - cySrc;       uy = cxSrc; }
    ctx.translate(-ux, -uy);
    ctx.drawImage(el, 0, 0, sw, sh);
    ctx.restore();

    // Duotone pass — composite fills over what we just drew.
    if ((pal.screen || pal.multiply) && palName !== 'normal') {
        ctx.save();
        if (pal.screen) { ctx.globalCompositeOperation = 'screen'; ctx.fillStyle = pal.screen; ctx.fillRect(0, 0, cw, ch); }
        if (pal.multiply) { ctx.globalCompositeOperation = 'multiply'; ctx.fillStyle = pal.multiply; ctx.fillRect(0, 0, cw, ch); }
        ctx.restore();
    }
};

Magnifier.prototype.draw = function () {
    var ctx = this.ctx, c = this.canvas;
    var cw = c.width / this._dpr, ch = c.height / this._dpr;
    ctx.setTransform(this._dpr, 0, 0, this._dpr, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.filter = 'none';
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, cw, ch);
    if (!this.src) return;

    this._paint(ctx, cw, ch, 1, this.ox, this.oy);

    if (this.guide) this._drawGuide(ctx, cw, ch);
    if (this.lensMode !== 'off' && this.lensActive) this._drawLens(ctx, cw, ch);
    if (this.onFrame) this.onFrame();
};

/**
 * Render the view WITHOUT the colour mode — same aim, same magnification, plain
 * pixels. Text recognition reads this, not the screen: a duotone or inverted
 * frame is exactly the input OCR is worst at, and the old build fed it the
 * tinted canvas, so turning on the mode that made the text readable to the EYE
 * was what stopped it being readable to the ENGINE.
 */
Magnifier.prototype.plainFrame = function () {
    var c = document.createElement('canvas');
    c.width = this.canvas.width; c.height = this.canvas.height;
    var ctx = c.getContext('2d');
    var cw = c.width / this._dpr, ch = c.height / this._dpr;
    ctx.setTransform(this._dpr, 0, 0, this._dpr, 0, 0);
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, cw, ch);
    if (this.src) this._paint(ctx, cw, ch, 1, this.ox, this.oy, 'normal');
    return c;
};

/**
 * Reading guide: everything outside a horizontal band is dimmed, so the line
 * you are on stays found while the picture moves under it.
 */
Magnifier.prototype._drawGuide = function (ctx, cw, ch) {
    var y = (this.lensMode !== 'off' && this.lensActive) ? this.lensY : ch / 2;
    if (this.guideY != null) y = this.guideY;
    var h = clamp(this.guideH, 24, ch);
    var top = clamp(y - h / 2, 0, Math.max(0, ch - h));
    ctx.save();
    ctx.filter = 'none';
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = 'rgba(0,0,0,.58)';
    ctx.fillRect(0, 0, cw, top);
    ctx.fillRect(0, top + h, cw, ch - top - h);
    ctx.strokeStyle = 'rgba(255,216,77,.9)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, top + .5); ctx.lineTo(cw, top + .5);
    ctx.moveTo(0, top + h - .5); ctx.lineTo(cw, top + h - .5);
    ctx.stroke();
    ctx.restore();
};

/**
 * The loupe. A round window that magnifies FURTHER on top of the base view —
 * the thing people mean by «лупа» and the one feature the surviving web
 * competitors (hostt 2–20×, imagetools) do build, always over a static image
 * only. Here it rides the same transform, so it works on live camera and on
 * screen capture too.
 */
Magnifier.prototype._drawLens = function (ctx, cw, ch) {
    var R = this.lensSize / 2;
    var lx = clamp(this.lensX, 0, cw), ly = clamp(this.lensY, 0, ch);
    var p = this.canvasToSource(lx, ly);

    ctx.save();
    ctx.beginPath();
    if (this.lensShape === 'square') ctx.rect(lx - R, ly - R, R * 2, R * 2);
    else ctx.arc(lx, ly, R, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    ctx.fillStyle = '#000';
    ctx.fillRect(lx - R, ly - R, R * 2, R * 2);
    // Paint into a translated frame so the lens centre lands on the pointer.
    ctx.translate(lx - cw / 2, ly - ch / 2);
    this._paint(ctx, cw, ch, this.lensZoom, p.x, p.y);
    ctx.restore();

    // Rim: two strokes so it reads on both light and dark content.
    ctx.save();
    ctx.beginPath();
    if (this.lensShape === 'square') ctx.rect(lx - R, ly - R, R * 2, R * 2);
    else ctx.arc(lx, ly, R, 0, Math.PI * 2);
    ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(0,0,0,.55)'; ctx.stroke();
    ctx.lineWidth = 1.5; ctx.strokeStyle = 'rgba(255,255,255,.95)'; ctx.stroke();
    ctx.restore();
};

/* ── readouts ────────────────────────────────────────────────────────────── */

Magnifier.prototype.percent = function () { return Math.round(this.total() * 100); };

/**
 * Magnification against the SOURCE pixel grid. Above 1.0 the picture is being
 * interpolated — there is no more detail to find, and saying so is the honest
 * version of the "40×" the market advertises.
 */
Magnifier.prototype.sourceRatio = function () {
    return (this._baseScale() * this.scale) / (this.frozen ? (this._frozenScale || 1) : 1);
};
Magnifier.prototype.interpolating = function () { return this.sourceRatio() > 1.02; };

Magnifier.prototype.toBlob = function (cb, type, q) {
    /* The frame the user is looking at, palette and all — because the palette
       lives in ctx.filter, not in canvas.style.filter. */
    try { this.canvas.toBlob(cb, type || 'image/png', q); } catch (e) { cb(null); }
};

Magnifier.MIN_SCALE = MIN_SCALE;
Magnifier.MAX_SCALE = MAX_SCALE;
Magnifier.PALETTES = PALETTES;
window.Magnifier = Magnifier;
})();

/**
 * magnifier-input.js (same file — one version, no skew between engine and the
 * gestures that drive it).
 *
 * Attaches pointer / touch / wheel / keyboard control to a Magnifier.
 *
 * The mobile decisions here are the ones that matter, because 62.5 % of
 * sessions on our closest public camera tool are mobile and 86.2 % are touch
 * (analytics_events, /camera-reader, 90 d to 07.09.2026):
 *
 *  - Pinch zooms about the MIDPOINT of the two fingers, so the thing you are
 *    pinching stays under your fingers instead of sliding to the centre.
 *  - One finger pans. Two fingers pinch. There is no mode to choose.
 *  - The loupe rides ABOVE the touch point by default, because a finger covers
 *    roughly the area a loupe would show — a lens drawn under the fingertip is
 *    a lens you cannot see.
 *  - `touch-action:none` is set by the tool CSS; without it Chrome Android
 *    scrolls the page instead of panning the picture.
 */
(function () {
'use strict';

function dist(a, b) { var dx = a.x - b.x, dy = a.y - b.y; return Math.sqrt(dx * dx + dy * dy); }

window.attachMagnifierInput = function (mag, el, opts) {
    opts = opts || {};
    var onGesture = opts.onGesture || function () {};
    /* Число активных указателей ВЫВОДИТСЯ из набора, а не считается отдельным
       счётчиком. Раздельный счётчик рассинхронизируется: любой не доехавший
       `pointerup` (жест, перехваченный браузером; палец, ушедший за край;
       отменённое касание) оставлял n === 2 навсегда, и с этого момента одиночное
       касание уходило в ветку щипка, где расстояние между «двумя» точками равно
       нулю, — то есть панорама пальцем МОЛЧА переставала работать до
       перезагрузки. Поймано на стенде: после тапа перед перетаскиванием
       касание перестало двигать картинку, хотя в одиночку работало. */
    var pts = Object.create(null);
    var last = null, pinch0 = 0, scale0 = 1, moved = 0;
    function count() { var k = 0; for (var _ in pts) k++; return k; }
    var lensLift = opts.lensLift == null ? 44 : opts.lensLift;

    function local(e) {
        var r = el.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
    }
    function list() { var a = []; for (var k in pts) a.push(pts[k]); return a; }

    function updateLens(p, fromTouch) {
        /* The reading guide follows the same pointer the loupe does, and it
           does so even when the loupe is off — it is an independent overlay. */
        if (mag.guide) { mag.guideY = p.y; mag.requestDraw(); }
        if (mag.lensMode === 'off') return;
        if (mag.lensMode === 'center') {
            var r = el.getBoundingClientRect();
            mag.lensX = r.width / 2; mag.lensY = r.height / 2; mag.lensActive = true;
        } else {
            mag.lensX = p.x;
            mag.lensY = p.y - (fromTouch ? lensLift + mag.lensSize / 2 : 0);
            mag.lensActive = true;
        }
        mag.requestDraw();
    }

    /* Внутри сцены живут кнопки (выбор источника, экран отказа камеры). Панорама
       НЕ должна начинаться на них — и дело не в удобстве: `setPointerCapture` на
       сцене перенаправляет указатель на неё, поэтому событие `click` уходит
       сцене, а не кнопке, и кнопка молча перестаёт работать. Ровно это и
       случилось: нажатие «Камера» не открывало камеру и не показывало отказа —
       обработчик просто не звался. */
    function onWidget(e) {
        var t = e.target;
        return !!(t && t.closest && t.closest('button,a,input,select,textarea,label,[role=button]'));
    }

    el.addEventListener('pointerdown', function (e) {
        if (onWidget(e)) return;
        try { el.setPointerCapture && el.setPointerCapture(e.pointerId); } catch (_) {}
        pts[e.pointerId] = local(e);
        moved = 0;
        var a = list(), n = count();
        if (n === 1) { last = a[0]; updateLens(a[0], e.pointerType === 'touch'); }
        else if (n === 2) { pinch0 = dist(a[0], a[1]); scale0 = mag.scale; }
    });

    el.addEventListener('pointermove', function (e) {
        var p = local(e);
        if (!(e.pointerId in pts)) {
            // Hover with a mouse: drive the loupe, do not pan.
            if (e.pointerType !== 'touch') updateLens(p, false);
            return;
        }
        pts[e.pointerId] = p;
        var a = list(), n = count();
        if (n >= 2) {
            var d = dist(a[0], a[1]);
            if (pinch0 > 0) {
                var mid = { x: (a[0].x + a[1].x) / 2, y: (a[0].y + a[1].y) / 2 };
                mag.setScale(scale0 * (d / pinch0), mid.x, mid.y);
            }
        } else if (last) {
            var dx = p.x - last.x, dy = p.y - last.y;
            moved += Math.abs(dx) + Math.abs(dy);
            if (mag.lensMode === 'pointer' && e.pointerType === 'touch') updateLens(p, true);
            else { if (mag.guide) { mag.guideY = p.y; } mag.panBy(dx, dy); }
            last = p;
        }
    });

    function up(e) {
        delete pts[e.pointerId];
        var a = list(), n = count();
        last = n === 1 ? a[0] : null;
        if (n < 2) pinch0 = 0;
        if (n === 0) {
            if (mag.lensMode === 'pointer' && e.pointerType === 'touch') { mag.lensActive = false; mag.requestDraw(); }
            if (moved > 6) onGesture('pan');
        }
    }
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('lostpointercapture', up);
    el.addEventListener('pointerleave', function (e) {
        if (e.pointerType !== 'touch' && mag.lensMode === 'pointer') { mag.lensActive = false; mag.requestDraw(); }
    });

    el.addEventListener('wheel', function (e) {
        if (!mag.src || onWidget(e)) return;
        e.preventDefault();
        var p = local(e);
        // Trackpads send many small deltas; the exponent keeps them smooth and
        // still lets a mouse notch move a useful amount.
        var f = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0016));
        mag.zoomBy(f, p.x, p.y);
        onGesture('wheel');
    }, { passive: false });

    el.addEventListener('dblclick', function (e) {
        if (onWidget(e)) return;
        var p = local(e);
        mag.zoomBy(mag.scale >= 4 ? 0.25 : 4, p.x, p.y);
        onGesture('dbl');
    });

    el.addEventListener('keydown', function (e) {
        if (!mag.src || onWidget(e)) return;
        var step = e.shiftKey ? 120 : 40, h = false;
        switch (e.key) {
            case 'ArrowLeft':  mag.panBy(step, 0); h = true; break;
            case 'ArrowRight': mag.panBy(-step, 0); h = true; break;
            case 'ArrowUp':    mag.panBy(0, step); h = true; break;
            case 'ArrowDown':  mag.panBy(0, -step); h = true; break;
            case '+': case '=': mag.zoomBy(1.25); h = true; break;
            case '-': case '_': mag.zoomBy(0.8); h = true; break;
        }
        // Цифры — по месту клавиши (AZERTY без Shift печатает «à &»); знаки выше
        // — как напечатаны, поэтому «-» и «_» AZERTY из ряда цифр остаются зумом.
        var kd = (h || e.ctrlKey || e.metaKey || e.altKey) ? '' : (window.__keyLetter ? window.__keyLetter(e) : e.key);
        if (kd === '0') { mag.fit(); h = true; }
        else if (kd === '1') { mag.actualSize(); h = true; }
        if (h) { e.preventDefault(); onGesture('key'); }
    });

    return {
        /** Live pointer bookkeeping — a guard asserts this returns to 0. */
        pointers: count,
        setLensLift: function (v) { lensLift = v; },
        showLensAtCenter: function () {
            var r = el.getBoundingClientRect();
            mag.lensX = r.width / 2; mag.lensY = r.height / 2; mag.lensActive = true; mag.requestDraw();
        }
    };
};
})();
